// pages/AllDepartments.js
import React, { useState, useEffect, useCallback } from 'react';
import {
    Plus, Search, Building2, Trash2, Edit3, Loader2,
    AlertCircle, Eye, X, Save, Calendar, Hash, ToggleLeft,
    CheckCircle2, XCircle, Users
} from 'lucide-react';

// ── API Config ─────────────────────────────────────────────────────────
const API_BASE = 'https://api.mobilink.tech/api/superAdmin';
const API_TOKEN = sessionStorage.getItem('adminToken') || sessionStorage.getItem('subAdminToken') || '';
const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${API_TOKEN}` };

// ── API Helpers ────────────────────────────────────────────────────────
const apiCall = async (url, options = {}) => {
    const res = await fetch(url, { ...options, headers });
    if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `Request failed (${res.status})`);
    }
    return res.json();
};

const fetchAllDepartments = () =>
    apiCall(`${API_BASE}/department/getall-departments`, { method: 'GET' });

// Uses POST because browsers block GET with body
const fetchDepartmentById = (id) =>
    apiCall(`${API_BASE}/department/getdepartmentbyid`, {
        method: 'POST',
        body: JSON.stringify({ id })
    });

const createDepartment = (payload) =>
    apiCall(`${API_BASE}/department/adddepartment`, {
        method: 'POST',
        body: JSON.stringify(payload)
    });

const updateDepartment = (id, payload) =>
    apiCall(`${API_BASE}/department/updatedepartment/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload)
    });

const deleteDepartment = (id) =>
    apiCall(`${API_BASE}/department/deletedepartment/${id}`, { method: 'DELETE' });

// ── Modal Wrapper ──────────────────────────────────────────────────────
const Modal = ({ isOpen, onClose, title, subtitle, children, maxWidth = 'max-w-lg' }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
            <div
                className={`w-full ${maxWidth} bg-white rounded-2xl shadow-2xl border border-orange-200 max-h-[90vh] flex flex-col animate-in zoom-in-95 duration-200`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Modal Header */}
                <div className="flex items-start justify-between gap-3 p-5 border-b border-orange-100 flex-shrink-0">
                    <div className="min-w-0">
                        <h2 className="text-base sm:text-lg font-bold text-gray-800">{title}</h2>
                        {subtitle && <p className="text-xs text-gray-500 mt-0.5 truncate">{subtitle}</p>}
                    </div>
                    <button
                        onClick={onClose}
                        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Modal Body */}
                <div className="overflow-y-auto flex-1">{children}</div>
            </div>
        </div>
    );
};

// ── Detail Row ─────────────────────────────────────────────────────────
const DetailRow = ({ icon: Icon, label, value, valueClass = '' }) => (
    <div className="flex items-start gap-3 py-3 border-b border-orange-100 last:border-0">
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center flex-shrink-0 mt-0.5">
            <Icon size={14} style={{ color: '#FF7D38' }} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">{label}</p>
            <div className={`text-sm mt-0.5 break-all ${valueClass}`}>{value || '—'}</div>
        </div>
    </div>
);

// ── Add / Edit Modal ───────────────────────────────────────────────────
const AddEditDepartmentModal = ({ isOpen, onClose, department, onSuccess }) => {
    const isEdit = !!department;
    const [formData, setFormData] = useState({ departmentName: '', status: 'active' });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [apiResponse, setApiResponse] = useState(null);

    // Reset / load data when modal opens or department changes
    useEffect(() => {
        if (!isOpen) {
            // Reset when closing
            setFormData({ departmentName: '', status: 'active' });
            setErrors({});
            setApiResponse(null);
            return;
        }

        if (isEdit && department?._id) {
            setFetching(true);
            fetchDepartmentById(department._id)
                .then((data) => {
                    if (data.success && data.department) {
                        setFormData({
                            departmentName: data.department.departmentName || '',
                            status: data.department.status || 'active'
                        });
                    }
                })
                .catch((err) => setApiResponse({ success: false, error: err.message }))
                .finally(() => setFetching(false));
        } else {
            setFormData({ departmentName: '', status: 'active' });
        }
    }, [isOpen, department, isEdit]);

    const validate = () => {
        const e = {};
        if (!formData.departmentName.trim()) e.departmentName = 'Department name is required';
        else if (formData.departmentName.trim().length < 2) e.departmentName = 'Name must be at least 2 characters';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        setApiResponse(null);

        try {
            const payload = {
                departmentName: formData.departmentName.trim(),
                status: formData.status
            };

            if (isEdit) {
                await updateDepartment(department._id, payload);
            } else {
                await createDepartment(payload);
            }

            setApiResponse({ success: true, message: `Department ${isEdit ? 'updated' : 'created'} successfully!` });

            setTimeout(() => {
                setLoading(false);
                onSuccess();
                onClose();
            }, 600);
        } catch (err) {
            setApiResponse({ success: false, error: err.message });
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? 'Edit Department' : 'Add New Department'}
            subtitle={isEdit ? `Update ${department?.departmentName || 'department'} details` : 'Create a new department'}
            maxWidth="max-w-md"
        >
            {fetching ? (
                <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <Loader2 size={28} className="animate-spin text-orange-500" />
                    <p className="text-sm text-gray-500">Loading department...</p>
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="p-5 space-y-5">
                    {/* Department Name */}
                    <div>
                        <label className="block text-sm font-medium mb-1.5 text-gray-600">
                            Department Name <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                            <Building2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                value={formData.departmentName}
                                onChange={(e) => setFormData({ ...formData, departmentName: e.target.value })}
                                className={`w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl bg-orange-50 border ${errors.departmentName ? 'border-red-300 focus:border-red-400' : 'border-orange-200 focus:border-orange-400'
                                    } focus:ring-2 focus:ring-orange-200 outline-none`}
                                placeholder="e.g., Human Resources, Finance"
                                disabled={loading}
                                autoFocus
                            />
                        </div>
                        {errors.departmentName && (
                            <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                                <XCircle size={12} /> {errors.departmentName}
                            </p>
                        )}
                    </div>

                    {/* Status */}
                    <div>
                        <label className="block text-sm font-medium mb-1.5 text-gray-600">Status</label>
                        <div className="relative">
                            <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <select
                                value={formData.status}
                                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                className="w-full pl-9 pr-3.5 py-2.5 text-sm rounded-xl appearance-none bg-orange-50 border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                                disabled={loading}
                            >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                            </select>
                        </div>
                    </div>

                    {/* API Response */}
                    {apiResponse && (
                        <div
                            className={`p-3 rounded-xl flex items-start gap-2 ${apiResponse.success
                                    ? 'bg-green-50 border border-green-200'
                                    : 'bg-red-50 border border-red-200'
                                }`}
                        >
                            {apiResponse.success ? (
                                <CheckCircle2 size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                            ) : (
                                <XCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                            )}
                            <p className={`text-sm ${apiResponse.success ? 'text-green-700' : 'text-red-700'}`}>
                                {apiResponse.success ? apiResponse.message : apiResponse.error}
                            </p>
                        </div>
                    )}

                    {/* Footer Actions */}
                    <div className="flex items-center justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium rounded-xl text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl disabled:opacity-70 bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm transition-all"
                        >
                            {loading ? (
                                <>
                                    <Loader2 size={14} className="animate-spin" /> Saving...
                                </>
                            ) : (
                                <>
                                    <Save size={14} /> {isEdit ? 'Update' : 'Create'} Department
                                </>
                            )}
                        </button>
                    </div>
                </form>
            )}
        </Modal>
    );
};

// ── View Details Modal ─────────────────────────────────────────────────
const ViewDepartmentModal = ({ isOpen, onClose, department }) => {
    const [loading, setLoading] = useState(false);
    const [details, setDetails] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!isOpen || !department?._id) {
            setDetails(null);
            setError(null);
            return;
        }

        setLoading(true);
        setError(null);
        fetchDepartmentById(department._id)
            .then((data) => {
                if (data.success && data.department) setDetails(data.department);
                else setError(data.message || 'Department not found');
            })
            .catch((err) => setError(err.message))
            .finally(() => setLoading(false));
    }, [isOpen, department]);

    const formatDate = (d) =>
        d ? new Date(d).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Department Details"
            subtitle={details?.departmentName || 'Loading...'}
            maxWidth="max-w-md"
        >
            <div className="p-5">
                {loading && (
                    <div className="flex flex-col items-center justify-center py-12 gap-3">
                        <Loader2 size={28} className="animate-spin text-orange-500" />
                        <p className="text-sm text-gray-500">Loading details...</p>
                    </div>
                )}

                {error && !loading && (
                    <div className="py-8 text-center">
                        <AlertCircle size={36} className="text-red-400 mx-auto mb-3" />
                        <p className="text-sm text-red-600 font-medium">{error}</p>
                    </div>
                )}

                {details && !loading && (
                    <div className="space-y-1">
                        <DetailRow icon={Hash} label="Department ID" value={details._id} valueClass="text-xs font-mono text-gray-500" />
                        <DetailRow
                            icon={Building2}
                            label="Department Name"
                            value={details.departmentName}
                            valueClass="font-semibold text-gray-800"
                        />
                        <DetailRow
                            icon={ToggleLeft}
                            label="Status"
                            value={
                                <span
                                    className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${details.status === 'active'
                                            ? 'bg-green-50 text-green-600 border-green-200'
                                            : 'bg-gray-50 text-gray-500 border-gray-200'
                                        }`}
                                >
                                    {details.status}
                                </span>
                            }
                        />
                        <DetailRow
                            icon={Calendar}
                            label="Created"
                            value={formatDate(details.createdAt)}
                            valueClass="text-xs text-gray-600"
                        />
                        <DetailRow
                            icon={Calendar}
                            label="Last Updated"
                            value={formatDate(details.updatedAt)}
                            valueClass="text-xs text-gray-600"
                        />
                    </div>
                )}

                {details && !loading && (
                    <div className="mt-5 pt-4 border-t border-orange-100 flex justify-end">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium rounded-xl bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 transition-colors"
                        >
                            Close
                        </button>
                    </div>
                )}
            </div>
        </Modal>
    );
};

// ── Delete Confirmation Modal ──────────────────────────────────────────
const DeleteConfirmModal = ({ isOpen, onClose, department, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const handleDelete = async () => {
        if (!department?._id) return;
        setLoading(true);
        setError(null);
        try {
            await deleteDepartment(department._id);
            onSuccess(department._id);
            onClose();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Delete Department"
            subtitle="This action cannot be undone"
            maxWidth="max-w-sm"
        >
            <div className="p-5 space-y-4">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                        <Trash2 size={18} className="text-red-500" />
                    </div>
                    <div className="min-w-0">
                        <p className="text-sm text-gray-700 leading-relaxed">
                            Are you sure you want to delete{' '}
                            <span className="font-semibold text-gray-900">
                                "{department?.departmentName}"
                            </span>
                            ? This action cannot be undone and all associated data will be permanently removed.
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex items-start gap-2">
                        <XCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-red-700">{error}</p>
                    </div>
                )}

                <div className="flex items-center justify-end gap-2 pt-2">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 text-sm font-medium rounded-xl text-gray-600 hover:bg-gray-50 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleDelete}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-xl disabled:opacity-70 bg-gradient-to-r from-red-500 to-red-600 hover:shadow-red-500/30 shadow-sm transition-all"
                    >
                        {loading ? (
                            <>
                                <Loader2 size={14} className="animate-spin" /> Deleting...
                            </>
                        ) : (
                            <>
                                <Trash2 size={14} /> Delete Department
                            </>
                        )}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

// ── Main Component ─────────────────────────────────────────────────────
export const AllDepartments = () => {
    const [departments, setDepartments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [error, setError] = useState(null);

    // Modal states
    const [addEditModal, setAddEditModal] = useState({ isOpen: false, department: null });
    const [viewModal, setViewModal] = useState({ isOpen: false, department: null });
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, department: null });

    const fetchDepartments = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchAllDepartments();
            setDepartments(data.departments || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchDepartments();
    }, [fetchDepartments]);

    const filteredDepartments = departments.filter(
        (d) =>
            d.departmentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            d.status?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Modal handlers
    const openAddModal = () => setAddEditModal({ isOpen: true, department: null });
    const openEditModal = (dept) => setAddEditModal({ isOpen: true, department: dept });
    const closeAddEditModal = () => setAddEditModal({ isOpen: false, department: null });

    const openViewModal = (dept) => setViewModal({ isOpen: true, department: dept });
    const closeViewModal = () => setViewModal({ isOpen: false, department: null });

    const openDeleteModal = (dept) => setDeleteModal({ isOpen: true, department: dept });
    const closeDeleteModal = () => setDeleteModal({ isOpen: false, department: null });

    const handleSaveSuccess = () => fetchDepartments();
    const handleDeleteSuccess = (deletedId) =>
        setDepartments((prev) => prev.filter((d) => d._id !== deletedId));

    return (
        <div className="min-h-screen">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-4 sm:p-5 rounded-2xl border border-orange-200 shadow-sm">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center">
                        <Building2 size={20} style={{ color: '#FF7D38' }} />
                    </div>
                    <div>
                        <h1 className="text-lg sm:text-xl font-bold text-gray-800">
                            Department Management
                        </h1>
                        <p className="text-xs sm:text-sm text-gray-500">
                            {departments.length} department{departments.length !== 1 ? 's' : ''} configured
                        </p>
                    </div>
                </div>
                <button
                    onClick={openAddModal}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 hover:shadow-orange-500/30 shadow-sm transition-all whitespace-nowrap"
                >
                    <Plus size={16} /> Add New Department
                </button>
            </div>

            {/* Search */}
            <div className="bg-white p-4 rounded-2xl border border-orange-200 shadow-sm">
                <div className="relative max-w-md">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search departments by name or status..."
                        className="w-full pl-9 pr-4 py-2.5 text-sm rounded-xl bg-orange-50 border border-orange-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-200 outline-none"
                    />
                </div>
            </div>

            {/* Error State */}
            {error && (
                <div className="p-4 rounded-xl bg-red-50 border border-red-200 flex items-center gap-3">
                    <AlertCircle size={18} className="text-red-500 flex-shrink-0" />
                    <p className="text-sm text-red-700 flex-1">{error}</p>
                    <button
                        onClick={fetchDepartments}
                        className="text-xs font-medium text-red-600 hover:underline"
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="flex flex-col items-center justify-center py-20">
                    <Loader2 size={36} className="animate-spin text-orange-500 mb-3" />
                    <p className="text-sm text-gray-500">Loading departments...</p>
                </div>
            )}

            {/* Empty State */}
            {!loading && !error && filteredDepartments.length === 0 && (
                <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-orange-200">
                    <Building2 size={48} className="text-gray-300 mb-4" />
                    <p className="text-gray-500 font-medium">
                        {searchTerm ? 'No departments match your search' : 'No departments found'}
                    </p>
                    {!searchTerm && (
                        <button
                            onClick={openAddModal}
                            className="mt-4 text-sm text-orange-500 font-medium hover:underline"
                        >
                            Create your first department →
                        </button>
                    )}
                </div>
            )}

            {/* Departments Grid */}
            {!loading && !error && filteredDepartments.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {filteredDepartments.map((dept) => (
                        <div
                            key={dept._id}
                            className="bg-white rounded-2xl border border-orange-200 p-5 hover:shadow-md transition-shadow flex flex-col gap-4"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center flex-shrink-0">
                                        <Building2 size={18} style={{ color: '#FF7D38' }} />
                                    </div>
                                    <div className="min-w-0">
                                        <h3 className="text-sm font-bold text-gray-800 truncate">
                                            {dept.departmentName}
                                        </h3>
                                        <p className="text-[10px] text-gray-400 truncate">
                                            ID: {dept._id.slice(-8)}
                                        </p>
                                    </div>
                                </div>
                                <span
                                    className={`flex-shrink-0 text-[10px] px-2 py-0.5 rounded-full border font-medium ${dept.status === 'active'
                                            ? 'bg-green-50 text-green-600 border-green-200'
                                            : 'bg-gray-50 text-gray-500 border-gray-200'
                                        }`}
                                >
                                    {dept.status}
                                </span>
                            </div>

                            <div className="flex items-center gap-4 text-xs text-gray-500">
                                <div className="flex items-center gap-1.5">
                                    <Users size={12} />
                                    <span>Department</span>
                                </div>
                                <span className="text-gray-300">|</span>
                                <span>Created {formatDate(dept.createdAt)}</span>
                            </div>

                            <div className="flex items-center gap-2 pt-3 border-t border-orange-100 mt-auto">
                                <button
                                    onClick={() => openEditModal(dept)}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100 transition-colors"
                                >
                                    <Edit3 size={13} /> Edit
                                </button>
                                <button
                                    onClick={() => openViewModal(dept)}
                                    className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition-colors"
                                >
                                    <Eye size={13} /> View
                                </button>
                                <button
                                    onClick={() => openDeleteModal(dept)}
                                    className="flex items-center justify-center w-9 h-9 rounded-lg bg-red-50 text-red-500 border border-red-200 hover:bg-red-100 transition-colors flex-shrink-0"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* ── Modals ────────────────────────────────────────────────── */}
            <AddEditDepartmentModal
                isOpen={addEditModal.isOpen}
                onClose={closeAddEditModal}
                department={addEditModal.department}
                onSuccess={handleSaveSuccess}
            />

            <ViewDepartmentModal
                isOpen={viewModal.isOpen}
                onClose={closeViewModal}
                department={viewModal.department}
            />

            <DeleteConfirmModal
                isOpen={deleteModal.isOpen}
                onClose={closeDeleteModal}
                department={deleteModal.department}
                onSuccess={handleDeleteSuccess}
            />
        </div>
    );
};

export default AllDepartments;